import { useEffect, useRef } from 'react'
import { Link } from '@inertiajs/react'
import { Activity, Search, Building2, Users, MessageSquare, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SortableHeaderButton } from '@/components/design-system/table/SortableHeaderButton'
import { StatCard } from '@/components/design-system/StatCard'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import TelemetryDateFilter from '../components/TelemetryDateFilter'
import TelemetryPagination from '../components/TelemetryPagination'
import { telemetryPathWithParams, telemetryRangeParams, useTelemetryNavigation, useSearchQuery } from '../hooks/useTelemetryNavigation'
import { formatDate, formatNumber } from '../utils'
import TokensWithBreakdown from '../components/TokensWithBreakdown'
import type { DateRange, Pagination, TokenBreakdown } from '../types'

interface BillingUsage {
  workersWithPlan: number
  workersInAlert: number
}

interface Organization {
  id: string
  name: string
  slug: string
  digitalWorkersCount: number
  totalSessions: number
  totalTokens: number
  lastActivityAt: string | null
  tokenBreakdown: TokenBreakdown | null
  billingUsage: BillingUsage
}

function BillingUsageBadge({ usage }: { usage: BillingUsage }) {
  if (usage.workersWithPlan === 0) {
    return <span className="text-muted-foreground">—</span>
  }
  if (usage.workersInAlert === 0) {
    return <Badge variant="success">Todos ok</Badge>
  }
  return (
    <Badge variant="warning">
      {usage.workersInAlert}/{usage.workersWithPlan} em alerta
    </Badge>
  )
}

interface Summary {
  totalOrganizations: number
  totalWorkers: number
  totalSessions: number
  totalTokens: number
  tokenBreakdown: TokenBreakdown | null
}

interface Props {
  summary: Summary
  organizations: Organization[]
  pagination: Pagination
  search: string
  dateFrom: string
  dateTo: string
  dateRange: DateRange
  sortBy: string
  sortDirection: 'asc' | 'desc'
}

function OrganizationsIndex({ summary, organizations, pagination, search, dateFrom, dateTo, dateRange, sortBy, sortDirection }: Props) {
  const [query, setQuery] = useSearchQuery(search)
  const normalizingSortRef = useRef(false)
  const basePath = '/cc/telemetry'
  const rangeParams = telemetryRangeParams(dateFrom, dateTo, dateRange)
  const { navigateWithParams, handleSort, handleDateChange } = useTelemetryNavigation(basePath, {
    search: search || undefined,
    ...rangeParams,
    sort_by: sortBy,
    sort_direction: sortDirection,
    page: pagination.page,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigateWithParams({ search: query || undefined, page: 1 })
  }

  const onSort = (key: string) => handleSort(sortBy, sortDirection, key)

  useEffect(() => {
    if (sortBy !== 'total_cost') {
      normalizingSortRef.current = false
      return
    }
    if (normalizingSortRef.current) return

    normalizingSortRef.current = true
    const params = new URLSearchParams(window.location.search)
    params.set('sort_by', 'name')
    params.set('sort_direction', 'asc')
    params.set('page', '1')
    window.location.replace(`${basePath}?${params.toString()}`)
  }, [basePath, sortBy])

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Organizações" value={formatNumber(summary.totalOrganizations)} icon={<Building2 />} colorScheme="gray" size="small" />
        <StatCard label="Digital Workers" value={formatNumber(summary.totalWorkers)} icon={<Users />} colorScheme="blue" size="small" />
        <StatCard label="Sessões" value={formatNumber(summary.totalSessions)} icon={<MessageSquare />} colorScheme="green" size="small" />
        <StatCard label="Tokens" value={<TokensWithBreakdown total={summary.totalTokens} breakdown={summary.tokenBreakdown} />} icon={<Zap />} colorScheme="violet" size="small" />
      </div>

      <div className="flex flex-col gap-3 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="h-6 w-6 text-foreground" />
            <h2 className="text-2xl font-bold text-foreground">Telemetria</h2>
            <span className="text-sm text-muted-foreground">({pagination.total})</span>
          </div>
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nome ou slug..." className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-64" />
            </div>
            <Button type="submit" variant="outline" size="sm">Buscar</Button>
          </form>
        </div>
        <TelemetryDateFilter dateFrom={dateFrom} dateTo={dateTo} dateRange={dateRange} onChange={handleDateChange} />
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-muted border-b border-border">
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <SortableHeaderButton label="Organização" sortKey="name" currentSortBy={sortBy} currentSortDirection={sortDirection} onSort={onSort} className="text-xs font-medium text-muted-foreground uppercase tracking-wider" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Slug</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Digital Workers</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <SortableHeaderButton label="Sessões" sortKey="total_sessions" currentSortBy={sortBy} currentSortDirection={sortDirection} onSort={onSort} className="text-xs font-medium text-muted-foreground uppercase tracking-wider" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <SortableHeaderButton label="Tokens" sortKey="total_tokens" currentSortBy={sortBy} currentSortDirection={sortDirection} onSort={onSort} className="text-xs font-medium text-muted-foreground uppercase tracking-wider" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Uso</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  <SortableHeaderButton label="Última Atividade" sortKey="last_activity_at" currentSortBy={sortBy} currentSortDirection={sortDirection} onSort={onSort} className="text-xs font-medium text-muted-foreground uppercase tracking-wider" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {organizations.map((org) => (
                <tr key={org.id} className="hover:bg-accent transition-colors">
                  <td className="px-4 py-3"><Link href={telemetryPathWithParams(`/cc/telemetry/organizations/${org.slug}`, rangeParams)} className="text-sm font-medium text-foreground hover:text-blue-600">{org.name}</Link></td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{org.slug}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{org.digitalWorkersCount}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{org.totalSessions}</td>
                  <td className="px-4 py-3 text-sm text-foreground"><TokensWithBreakdown total={org.totalTokens} breakdown={org.tokenBreakdown} /></td>
                  <td className="px-4 py-3 text-sm"><BillingUsageBadge usage={org.billingUsage} /></td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{org.lastActivityAt ? formatDate(org.lastActivityAt) : '—'}</td>
                </tr>
              ))}
              {organizations.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhuma organização com digital workers encontrada.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <TelemetryPagination pagination={pagination} onPageChange={(page) => navigateWithParams({ page })} />
      </div>
    </div>
  )
}

OrganizationsIndex.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default OrganizationsIndex
